import React, { useEffect, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import  useLocale  from "../../src/locales/index";
import { setActiveCalculator } from "../redux/calculations/calculaterSlice";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import numberSuffixes from "../utiles/numberSuffixes";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
const CarBarChart = ({
  carBuyCost,
  carLeaseCost,
  purchasePrice,
  monthlyBuyPayment,
  totalInterest,
  totalLeasePayments,
  securityDeposit,
  downPayment,
}) => {
  const { textData } = useLocale();
  const dispatch = useDispatch();
    const { i18n } = useTranslation();
  
  const langCode = i18n.language || "default";
  const suffixSet = numberSuffixes[langCode] || numberSuffixes["default"];


  const formatCurrency = (value) => {
    const language = i18n.language || "en-IN"; 
    return value
      ? Math.round(value).toLocaleString(language, {
          maximumFractionDigits: 0,
        })
      : "0";
  };

  // Bar chart data for Car Buy Cost vs Car Lease Cost
  const barData = useMemo(() => {
    return {
      labels: [
        ` ${textData.currency}${formatCurrency(carBuyCost)}`,
        ` ${textData.currency}${formatCurrency(carLeaseCost)}`
      ],
      datasets: [
        {
          label: "Cost",
          data: [carBuyCost, carLeaseCost],
          backgroundColor: ["#57C6F4", "#FF8566"],
          barThickness: 60,
          borderTopLeftRadius: 6,
          borderRadius: 6,
          borderTopRightRadius: 6
        }
      ],
    };
  }, [carBuyCost, carLeaseCost]);

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: "#000000",
          font: {
            size:' 12 !important',
            weight: 500,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "#EEEEEE",
          drawBorder: false,
        },
        ticks: {
          padding: 10,
          callback: function (value) {
            if (value >= 10000000) return (value / 10000000).toFixed(0) + suffixSet.crore;
            if (value >= 100000) return (value / 100000).toFixed(0) + suffixSet.lakh;
            if (value >= 1000) return (value / 1000).toFixed(0) + suffixSet.thousand;
            return value.toLocaleString(i18n.language);
          },
       
          color: "#888888",
          font: {
            size: 10,
          },
         
        },
      },
    },
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: 20,
        bottom: 10,
      },
    },
  };

useEffect(() =>{
  dispatch(setActiveCalculator(""))
})
 
  return (
    <div className="bg-gray-50   pt-6 rounded-lg">
      <div className="h-[320px] w-[100%] sm:w-[85%] lg:w-[256px] flex justify-center mb-2 mx-auto">
      <Bar data={barData} options={barChartOptions} />
      </div>
      
      {/* Cost Summary */}
      <div className="mb-2">
        <div className="flex items-center py-5 border-b border-solid border-gray-light">
          <div className="h-5 w-5 mr-2 rounded-[4px] bg-[#57C6F4]"></div>
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between font-medium">
         
            {textData.chartLabels.carBusyCost}
            <span>{textData.currency}{formatCurrency(carBuyCost)}</span>
          </div>
        </div> 
        
        <div className="flex items-center py-5 border-b border-solid border-gray-light">
          <div className="h-5 w-5 mr-2 rounded-[4px] bg-[#FF8566]"></div>
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between font-medium">
                 {textData.chartLabels.carLeast}

            <span>{textData.currency}{formatCurrency(carLeaseCost)}</span>
          </div>
        </div>
      </div>
      
      {/* Buy Details */}
      <div className=" pt-4">
        <div className="flex items-center pb-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
           {textData.chartLabels.carBuyTotal}

            <span>{textData.currency}{formatCurrency(carBuyCost)}</span>
          </div>
        </div>
        
        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
            {textData.chartLabels.purchasePrice}

            <span>{textData.currency}{formatCurrency(purchasePrice)}</span>
          </div>
        </div>
        
        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
    {textData.chartLabels.monthlyPay}
            <span>{textData.currency}{formatCurrency(monthlyBuyPayment)}</span>
          </div>
        </div>
        
        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
                {textData.chartLabels.totalIntrest}

            <span>{textData.currency}{formatCurrency(totalInterest)}</span>
          </div>
        </div>
      </div>
      
      {/* Lease Details */}
      <div className="  mt-2">
        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
                       {textData.chartLabels.carLeaseTotal}

            <span>{textData.currency}{formatCurrency(carLeaseCost)}</span>
          </div>
        </div>
        
        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
                {textData.chartLabels.totalPayment}

            <span>{textData.currency}{formatCurrency(totalLeasePayments)}</span>
          </div>
        </div>
        
        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
                   {textData.chartLabels.securityDeposite}

            <span>{textData.currency}{formatCurrency(securityDeposit)}</span>
          </div>
        </div>
        
        <div className="flex items-center py-[10px]">
          <div className="flex-grow text-left text-sm sm:text-sm text-black flex justify-between">
             {textData.chartLabels.downPayment}

            <span>{textData.currency}{formatCurrency(downPayment)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarBarChart;